<template>
    <card cardBodyClasses="p-0">
        <b-row>
            <b-col cols="10">
                <h4 class="card-subtitle" style="padding-top: 15px; padding-left: 15px">
                    {{ cardTitle() }}
                </h4>
            </b-col>
            <b-col>
                <b-button v-if="!getByContactMode && !readOnly" v-b-modal="'addContact' + this.$attrs.id"
                    class="btn btn-sm btn-success">
                    <i class="fas fa-user-plus"></i>
                </b-button>
            </b-col>
        </b-row>
        <ul v-if="emergencyContacts && emergencyContacts.length > 0 && getByContactMode"
            class="list-group list-group-flush">
            <li class="list-group-item">
                <b-row>
                    <b-col>
                        <b>Patient Name</b>
                    </b-col>
                </b-row>
            </li>
            <li v-for="c in emergencyContacts" :key="c.id" class="list-group-item">
                <b-row>
                    <b-col>
                        <router-link :to="{ name: 'PatientDashboard', params: { id: c.patient.id } }">
                            {{ c.patient.displayName }}
                        </router-link>
                    </b-col>
                </b-row>
            </li>
        </ul>
        <ul v-else-if="emergencyContacts && emergencyContacts.length > 0" class="list-group list-group-flush">
            <li class="list-group-item">
                <b-row>
                    <b-col>
                        <b>Contact Name</b>
                    </b-col>
                    <b-col>
                        <b>HIPAA Authorized?</b>
                    </b-col>
                    <b-col>
                        <b>Relationship</b>
                    </b-col>
                    <b-col v-if="!readOnly" cols="2"></b-col>
                </b-row>
            </li>
            <li v-for="c in emergencyContacts" :key="c.id" class="list-group-item">
                <b-row>
                    <b-col>
                        <router-link :to="{ name: 'PatientDashboard', params: { id: c.contact.id } }">
                            {{ c.contact.displayName }}
                        </router-link>
                    </b-col>
                    <b-col>
                        {{ c.hipaaAuthorized ? "Yes" : "No" }}
                    </b-col>
                    <b-col>
                        {{ c.relationship }}
                    </b-col>
                    <b-col v-if="!readOnly" cols="2">
                        <button class="btn btn-sm btn-danger m-0" title="Remove Contact" @click="removeContact(c)">
                            <i class="fas fa-user-times"></i>
                        </button>
                    </b-col>
                </b-row>
            </li>
        </ul>
        <div v-else style="padding: 15px">
            No {{ getByContactMode ? "Linked Patients" : "Emergency Contacts" }} On File
        </div>
        <b-modal :id="'addContact' + this.$attrs.id" hide-footer title="Add Emergency Contact" size="lg">
            <SearchComponent :id="patient && +patient.id" v-model="newContact" :create-new-object="true"
                createNewLabel="Create New Patient" label="Emergency Contact" search-u-r-l="/Patient" required
                @createNewObject="showPatientForm = true" @input="contactUpdate" />
            <div v-show="showPatientForm">
                <form>
                    <b-form-row>
                        <b-col cols="6">
                            <fg-input v-model="newContact.firstName" label="First Name" />
                        </b-col>
                        <b-col cols="6">
                            <fg-input v-model="newContact.lastName" label="Last Name" />
                        </b-col>
                        <b-col cols="6">
                            <fg-input v-model="newContact.cellNumber" label="Cell Number" />
                        </b-col>
                        <b-col cols="6">
                            <fg-input v-model="newContact.phoneNumber" label="Phone Number" />
                        </b-col>
                        <b-col cols="12">
                            <fg-input v-model="newContact.emailAddress" label="Email Address" />
                        </b-col>
                    </b-form-row>
                </form>
            </div>
            <div v-show="newContact.id">
                <h5>{{ newContact.displayName }}</h5>
                <span>Phone: {{ newContact.phone ? newContact.phone.nationalFormat : "" }}</span><br />
                <span>Cell: {{ newContact.cell ? newContact.cell.nationalFormat : "" }}</span><br />
                <span>Email: {{ newContact.emailAddress }}</span>
            </div>
            <b-row>
                <b-col>
                    <fg-input v-model="relationship" label="Relationship" />
                </b-col>
            </b-row>
            <div class="float-right">
                <checkbox v-model="hipaaReleased" inline>Consent to HIPAA Release?</checkbox>
                <b-button variant="success" @click="saveContact">Save</b-button>
            </div>
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import axios, { AxiosError } from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Patient } from "@/models/Patient";
import EnumSelector from "@/components/EnumSelector.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { EmergencyContact } from "@/models/EmergencyContact";
import PatientFormComponent from "./PatientFormComponent.vue";

@Component({
    name: "EmergencyContactList",
    components: {
        SearchComponent,
        EnumSelector,
        Checkbox,
        PatientFormComponent,
    },
})
export default class EmergencyContactList extends Vue {
    @Prop() private patient: any;
    @Prop({ type: Boolean, default: false }) private getByContactMode!: boolean;
    @Prop({ type: Boolean, default: false }) private readOnly!: boolean;
    private hipaaReleased: boolean = false;
    protected emergencyContacts: EmergencyContact[] = [];
    private newContact: Patient = new Patient();
    protected showPatientForm: boolean = false;
    private relationship: string = "";

    cardTitle() {
        return this.getByContactMode ? "Linked Patients" : "Emergency Contacts";
    }

    created() {
        console.log("%c Created Emergency Contact Component", "color: green");
        this.fetchPatient();
    }

    contactUpdate() {
        this.showPatientForm = false;
    }

    removeContact(contact: EmergencyContact) {
        contact.active = false;
        axios.post("/Patient/EmergencyContact", contact)
            .catch(err => this.$notification(NotificationOptions.error(err)))
            .finally(this.fetchPatient);
    }

    saveContact() {
        if (this.newContact.id) {
            this.finalSave();
            return;
        }
        const config = { params: { needsDob: false } };
        axios
            .post<{ patient: Patient; warning: string }>("/Patient", this.newContact, config)
            .then((response) => {
                this.newContact = Object.assign(new Patient(), response.data.patient);
                this.finalSave();
            })
            .catch((error: AxiosError) => {
                this.$notification(NotificationOptions.error(error));
                console.error(error);
            });
    }

    finalSave() {
        let data = new EmergencyContact();
        data.patientId = this.patient.id;
        data.contactPatientId = +this.newContact.id;
        data.active = true;
        data.hipaaAuthorized = this.hipaaReleased;
        data.relationship = this.relationship;
        axios.post("/Patient/EmergencyContact", data).then(_response => {
            this.$bvModal.hide("addContact" + this.$attrs.id);
            this.newContact = new Patient();
            this.hipaaReleased = false;
            this.showPatientForm = false;
            this.relationship = "";
            this.fetchPatient();

        }).catch((error: AxiosError) => {
            this.$notification(
                NotificationOptions.error(error),
            );
            console.error(error);
        });
    }

    @Watch("patient")
    fetchPatient() {
        if (!this.patient?.id) {
            return;
        }
        let url = `/Patient/EmergencyContact/${this.patient?.id}`;
        if (this.getByContactMode) {
            url += "/Patients";
        }

        axios
            .get(url)
            .then((response) => {
                this.emergencyContacts = response.data.map((d: any) => new EmergencyContact(d));
            })
            .catch((error) => {
                console.error("Error while loading the Emergency Contacts", {
                    error,
                    response: error?.response,
                });
                this.$notification(NotificationOptions.error(error));
            });
    }
}
</script>

<style scoped></style>
